import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  AfterViewInit,
  HostListener,
} from '@angular/core';

@Component({
    selector: 'single-select-input',
    templateUrl: './single-select-input.component.html',
    standalone: false
})
export class SingleSelectInputComponent<T> implements AfterViewInit {
  @Input() label = '';
  @Input() floatingLabel = false;
  @Input() options: T[] = [];
  @Input() value: T | null = null;
  @Input() displayFn: (value: T) => string = (value) => String(value);
  @Input() noOptionsMessage = 'No options available';

  @Output() valueChange = new EventEmitter<T>();

  @ViewChild('detailsElementRef', { static: true })
  detailsElementRef!: ElementRef<HTMLDetailsElement>;

  /** Determines whether dropdown opens upward or downward */
  dropdownOpenDirection: 'up' | 'down' = 'down';

  /** Must match max-h-48 (12rem ≈ 192px) */
  private readonly dropdownMaxHeightPx = 192;

  get hasValue(): boolean {
    return this.value !== null;
  }

  get selectedLabel(): string {
    return this.value !== null ? this.displayFn(this.value) : '';
  }

  ngAfterViewInit(): void {
    this.detailsElementRef.nativeElement.addEventListener('toggle', () => {
      if (this.detailsElementRef.nativeElement.open) {
        this.updateDropdownOpenDirection();
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const targetElement = event.target as HTMLElement | null;
    if (!targetElement) {
      return;
    }

    const detailsElement = this.detailsElementRef.nativeElement;
    if (detailsElement.open && !detailsElement.contains(targetElement)) {
      this.closeDropdown();
    }
  }

  selectOption(option: T): void {
    this.valueChange.emit(option);
    this.closeDropdown();
  }

  onSummaryClick(event: MouseEvent): void {
    event.preventDefault();

    const detailsElement = this.detailsElementRef.nativeElement;

    if (detailsElement.open) {
      // CLOSE on second click
      detailsElement.removeAttribute('open');
      return;
    }

    // OPEN (no flicker)
    this.updateDropdownOpenDirection();
    detailsElement.open = true;
  }

  closeDropdown(): void {
    const detailsElement = this.detailsElementRef.nativeElement;
    detailsElement.removeAttribute('open');
  }

  private updateDropdownOpenDirection(): void {
    const detailsBoundingRect =
      this.detailsElementRef.nativeElement.getBoundingClientRect();

    const viewportHeight = window.innerHeight;
    const availableSpaceBelow = viewportHeight - detailsBoundingRect.bottom;
    const availableSpaceAbove = detailsBoundingRect.top;

    this.dropdownOpenDirection =
      availableSpaceBelow < this.dropdownMaxHeightPx &&
      availableSpaceAbove > availableSpaceBelow
        ? 'up'
        : 'down';
  }
}
